import React, { useEffect } from 'react';
import { UserSelection } from './UserSelection';
import { ColorSelectionUI } from '../ReusableColorSwatchesContainer';
import { IProduct, TOptionName, TUserSelection, UpdateSelectionFunc } from '../../types/PDP';
import { getOnlineSalePricing, heapTrack, isTagPresent } from 'src/scripts/utils/helpers';
import { computeShopByColorConfig } from '../../../_shared/ui-components/ShopByColor/helpers';
import ShopByColorUI from '../../../_shared/ui-components/ShopByColor/ui';
import { filter, partition, sortBy } from 'lodash';
import { processBadgeDiscount } from 'src/scripts/templates/product-listing-page/PlpUtils/ProcessBadgeDiscount';

export const SwatchVariantSelector = (props: SwatchVariantSelectorProps) => {
  const {
    optionValues,
    displayPrice,
    actualPrice,
    isASet,
    updateSelection,
    productUserSelection,
    optionName,
    product,
    productIndex,
  } = props;

  const { options } = product;
  const availableColorNames = options.Color;
  const filteredProducts = filter(product.productDataByColorName, (product) => {
    return product.onlineStoreUrl !== null;
  }).filter((product) => availableColorNames.includes(product.colorName));

  const matchesSortScoreOriginalOrder = sortBy(filteredProducts, (item) =>
    product.options.Color.findIndex((i) => i === item.colorName)
  );

  const [limitedColorsProduct, coreColorsProduct] = partition(matchesSortScoreOriginalOrder, (product) =>
    isTagPresent(product.tags, 'colorwaytype:limited')
  );
  const limitedColors = limitedColorsProduct.map((product) => product.colorName);
  const coreColors = coreColorsProduct.map((product) => product.colorName);

  const colorOptions =
    limitedColors.length === 0 || coreColors.length === 0
      ? [{ colorGroupName: '', colorArray: optionValues }]
      : [
          { colorGroupName: 'Core', colorArray: coreColors },
          { colorGroupName: 'Limited', colorArray: limitedColors },
        ];

  const productDataForColor = product.productDataByColorName[productUserSelection.Color || ''];
  const { tags } = productDataForColor || {};
  const config = tags && computeShopByColorConfig({ tags });

  const setItemActualPrice = isASet ? productDataForColor?.variants?.[0]?.price : undefined;

  const diffPriceForSet = getOnlineSalePricing(tags, setItemActualPrice);

  const setItemDisplayPrice =
    isASet && diffPriceForSet ? diffPriceForSet : productDataForColor?.variants?.[0]?.displayPrice ?? null;

  const discount =
    isASet && setItemActualPrice && setItemDisplayPrice
      ? processBadgeDiscount(setItemActualPrice, setItemDisplayPrice)
      : !isASet
      ? processBadgeDiscount(displayPrice, actualPrice)
      : null;

  const limitedSwatches = limitedColors.length < 1 || coreColors.length < 1 ? 0 : 1;

  useEffect(() => {
    heapTrack('PDP - Visit - PDP swatch differentiator', {
      limitedSwatches,
    });
  }, [limitedSwatches]);

  return (
    <>
      {colorOptions.map((swatchCollection) => {
        const groupName = swatchCollection.colorGroupName;
        const selectedColor = productUserSelection[optionName] || '';

        const isGroupActive = swatchCollection.colorArray.includes(selectedColor);
        const swatchGroupName =
          colorOptions.length > 1 ? (
            isGroupActive ? (
              <>
                {groupName}: {selectedColor}
                {config && <ShopByColorUI {...config} showColorInLink={false} />}
              </>
            ) : (
              groupName
            )
          ) : (
            <>
              {selectedColor}
              {config && <ShopByColorUI {...config} showColorInLink={false} />}
            </>
          );

        return (
          <>
            <p
              className={isASet ? 'color-option--set' : ''}
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <span className="body black">
                {swatchGroupName}
                {discount && isGroupActive ? ` - ${discount}` : ''}
              </span>
              <span className="body d-block d-md-none color-size">
                {swatchCollection.colorArray.length === 1
                  ? `${swatchCollection.colorArray.length} Color`
                  : `${swatchCollection.colorArray.length} Colors`}
              </span>
            </p>
            <div className={`options-wrapper options-wrapper--color ${`${groupName.toLowerCase()}-swatches`}`}>
              {swatchCollection.colorArray.map((color) => {
                const onSelect = () => {
                  updateSelection(productIndex, optionName, color);
                };
                const variants = product.productDataByColorName[color]?.variants;
                const swatchSections = product.swatchUiDataByColor[color]?.sections ?? [];

                return (
                  <UserSelection
                    key={color}
                    productUid={productIndex}
                    optionName={optionName}
                    value={color}
                    product={product}
                    userSelection={productUserSelection}
                    onSelect={onSelect}
                  >
                    <ColorSelectionUI
                      colorValue={color}
                      isASet={isASet}
                      userSelection={productUserSelection}
                      splitAdvanceOrderBadge={true}
                      variants={variants}
                      swatchSections={swatchSections}
                      productDataByColorName={product.productDataByColorName}
                    />
                  </UserSelection>
                );
              })}
            </div>
          </>
        );
      })}
    </>
  );
};

export interface SwatchVariantSelectorProps {
  optionValues: string[];
  displayPrice: number;
  actualPrice: number;
  isASet: boolean;
  updateSelection: UpdateSelectionFunc;
  productUserSelection: TUserSelection;
  optionName: TOptionName;
  product: IProduct;
  productIndex: number;
}
